import { Close, InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { getReadableAnimalAdoptionOfferType } from '../../../domain/Animal/AnimalDisplay'
import { AnimalAdoptionDtoOfferType } from '../../../interactors/gen/backendClient'
import { isValidPastDateCoherence } from '../../../utils/date/isValidPastDateCoherence'
import { setOpacity } from '../../../utils/setOpacity'
import { ColoredChipSelectField } from '../../common/ColoredChipSelectField'
import { ControlledDateField } from '../../common/ControlledDateField'
import { ControlledSelectField } from '../../common/ControlledSelectField'
import { spacingItem, theme } from '../../theme'
import type { AdoptionContext } from '../FastLaunchProcedureDialog'

type AdoptionInfoFormParams = {
  offerType?: AnimalAdoptionDtoOfferType
  priceInEuros?: number
  hasDeposit: boolean
  depositDate?: string
  depositAmount?: number
  depositStatus?: 'true' | 'false'
  prepareDocuments: boolean
}

interface Props {
  submitStep: (data: { adoptionContext: AdoptionContext }) => void
  close: () => void
  isPremium: boolean
  defaultValues?: AdoptionContext
}

export const AdoptionInfoStep: React.FC<Props> = ({ submitStep, close, isPremium, defaultValues }) => {
  const { control, register, handleSubmit, formState, watch, setValue } = useForm<AdoptionInfoFormParams>({
    defaultValues: {
      offerType: defaultValues?.offerType ?? AnimalAdoptionDtoOfferType.HealthCostCompensation,
      priceInEuros: defaultValues?.offerType === AnimalAdoptionDtoOfferType.Donation ? 0 : defaultValues?.priceInEuros,
      hasDeposit: defaultValues?.hasDeposit ?? false,
      depositDate: defaultValues?.depositDate,
      depositAmount: defaultValues?.depositAmount,
      depositStatus: defaultValues?.depositStatus === false ? 'false' : 'true',
      prepareDocuments: isPremium ? defaultValues?.prepareDocuments ?? true : false,
    },
  })

  const { errors } = formState
  const offerType = watch('offerType')
  const hasDeposit = watch('hasDeposit')
  const prepareDocuments = watch('prepareDocuments')
  const isDonation = offerType === AnimalAdoptionDtoOfferType.Donation

  useEffect(() => {
    if (!offerType) {
      setValue('offerType', AnimalAdoptionDtoOfferType.HealthCostCompensation)
    }
  }, [offerType, setValue])

  useEffect(() => {
    if (isDonation) {
      setValue('priceInEuros', 0)
    }
  }, [isDonation, setValue])

  useEffect(() => {
    if (!hasDeposit) {
      setValue('depositDate', undefined)
      setValue('depositAmount', undefined)
      setValue('depositStatus', 'true')
    }
  }, [hasDeposit, setValue])

  const onSubmit = (data: AdoptionInfoFormParams) => {
    const adoptionContext: AdoptionContext = {
      offerType: data.offerType,
      priceInEuros:
        data.offerType === AnimalAdoptionDtoOfferType.Donation || data.priceInEuros == null ? 0 : data.priceInEuros,
      hasDeposit: data.hasDeposit,
      depositDate: data.hasDeposit ? data.depositDate : undefined,
      depositAmount: data.hasDeposit ? data.depositAmount : undefined,
      depositStatus: data.hasDeposit ? data.depositStatus === 'true' : undefined,
      prepareDocuments: isPremium ? data.prepareDocuments : false,
    }

    submitStep({ adoptionContext })
  }

  return (
    <>
      <DialogTitle sx={{ fontSize: 24, fontWeight: 700 }}>Configurer l&apos;adoption</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <IconButton sx={{ position: 'absolute', top: 10, right: 10 }} onClick={close}>
            <Close />
          </IconButton>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5, fontStyle: 'italic' }}>
            <Typography variant="body2" color="text.primary">
              👉 Lancez une procédure d&apos;adoption
            </Typography>
            <Typography variant="body2" color="text.primary">
              👉 Complétez le suivi de la procédure
            </Typography>
            <Typography variant="body2" color="warning.main">
              👑 Premium - générez et envoyez vos documents à partir de vos modèles et validez leur réception
            </Typography>
            <Typography variant="body2" color="text.primary">
              👉 Permettez à l&apos;adoptant de régler les frais d&apos;adoption en ligne depuis un espace Petso dédié
            </Typography>
          </Box>

          <Box sx={{ ...spacingItem }}>
            <ControlledSelectField
              control={control}
              error={errors.offerType}
              fieldName="offerType"
              label="Nature de l'offre"
              options={Object.values(AnimalAdoptionDtoOfferType).map((field: AnimalAdoptionDtoOfferType) => ({
                label: getReadableAnimalAdoptionOfferType(field),
                value: field,
              }))}
              requiredRule="Vous devez renseigner la nature de l'offre."
              size="small"
            />
          </Box>

          <TextField
            id="price-input"
            label="Montant de l'adoption"
            type="number"
            inputProps={{
              step: '0.01',
              inputMode: 'decimal',
              pattern: '[0-9]*',
            }}
            fullWidth
            {...register('priceInEuros', {
              valueAsNumber: true,
              validate: (value) => {
                if (isDonation) return true
                if (value === undefined || value === null || Number.isNaN(value)) {
                  return "Le montant de l'adoption est requis"
                }
                if (value < 0) {
                  return 'Le montant doit être positif'
                }
                return true
              },
            })}
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
            }}
            InputLabelProps={{ shrink: true }}
            sx={spacingItem}
            size="small"
            disabled={isDonation}
            error={!!errors.priceInEuros}
            helperText={errors.priceInEuros?.message}
          />

          <Box sx={{ ...spacingItem, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography fontWeight={600}>Un acompte a-t-il déjà été réalisé ?</Typography>
            <Controller
              name="hasDeposit"
              control={control}
              render={({ field }) => (
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} color="primary" />
              )}
            />
          </Box>

          {hasDeposit && (
            <Box
              sx={{
                ...spacingItem,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <ControlledDateField
                control={control}
                error={errors.depositDate}
                fieldName="depositDate"
                label="Date de l'acompte"
                requiredRule="Vous devez renseigner la date de l'acompte."
                size="small"
                validate={(value) => isValidPastDateCoherence(value) || value === null}
              />
              <TextField
                id="deposit-amount-input"
                label="Montant de l'acompte"
                type="number"
                inputProps={{
                  step: '0.01',
                  inputMode: 'decimal',
                  pattern: '[0-9]*',
                }}
                fullWidth
                {...register('depositAmount', {
                  valueAsNumber: true,
                  validate: (value) => {
                    if (!hasDeposit) return true
                    if (value === undefined || value === null || Number.isNaN(value)) {
                      return "Le montant de l'acompte est requis"
                    }
                    if (value < 0) {
                      return 'Le montant doit être positif'
                    }
                    return true
                  },
                })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                error={!!errors.depositAmount}
                helperText={errors.depositAmount?.message}
              />
              <ColoredChipSelectField
                control={control}
                error={errors.depositStatus}
                fieldName="depositStatus"
                label="État de l'acompte"
                options={[
                  { label: 'Validé', value: 'true', color: theme.palette.success.main },
                  { label: 'En attente', value: 'false', color: theme.palette.warning.main },
                ]}
                requiredRule=""
                size="small"
              />
            </Box>
          )}

          <Box
            sx={{
              ...spacingItem,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              padding: 2,
              borderRadius: 2,
              backgroundColor: setOpacity(theme.palette.info.main, 0.08),
              border: `1px solid ${setOpacity(theme.palette.info.main, 0.3)}`,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700} color="text.primary">
                Signature en ligne - Générer et envoyer à l&apos;adoptant le certificat et le contrat via Petso
              </Typography>
              {prepareDocuments ? (
                <Typography variant="body2" fontStyle="italic" color="text.primary" sx={{ mt: 0.5 }}>
                  Vous pourrez choisir le modèle de document à utiliser pour préparer vos documents dans les prochaines
                  étapes.
                </Typography>
              ) : (
                <Typography variant="body2" fontStyle="italic" color="text.primary" sx={{ mt: 0.5 }}>
                  Les documents seront considérés comme remis en mains propres (aucun envoi via Petso pour cette
                  procédure). Vous pourrez stocker vos documents dans l&apos;espace procédure qui sera créé.
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={isPremium ? '' : 'Réservé au plan Petso Premium'}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Typography fontWeight={700} color="text.primary">
                    {prepareDocuments ? 'Activé' : 'Désactivé'}
                  </Typography>
                  <Switch
                    checked={prepareDocuments}
                    onChange={(e) => setValue('prepareDocuments', e.target.checked)}
                    color="warning"
                    disabled={!isPremium}
                    inputProps={{
                      title: isPremium ? '' : 'Réservé au plan Petso Premium',
                    }}
                  />
                  <span role="img" aria-label="crown">
                    👑
                  </span>
                  <InfoOutlined sx={{ color: theme.palette.info.main }} fontSize="small" />
                </span>
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="outlined" onClick={close}>
            Annuler
          </Button>
          <Button variant="contained" type="submit">
            Suivant
          </Button>
        </DialogActions>
      </form>
    </>
  )
}
